import React, { useEffect, useMemo, useRef, useState } from "react";
import { api, formatApiError } from "@/api";
import { PageHeader } from "@/pages/admin/AdminDashboard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  BellRing, Send, RefreshCw, Play, Trash2, RotateCcw, Mail, Smartphone,
  Inbox, AlertTriangle, CheckCircle2, Clock, Activity, Layers, MessageSquare,
} from "lucide-react";

const STATUS_TABS = [
  { key: "all", label: "Tout" },
  { key: "queued", label: "En file" },
  { key: "retry", label: "Retry" },
  { key: "sending", label: "En cours" },
  { key: "sent", label: "Envoyés" },
  { key: "dead", label: "Échoués" },
];

const STATUS_STYLE = {
  queued: "bg-[#002FA7] text-white",
  retry: "bg-[#F59E0B] text-white",
  sending: "bg-[#7C3AED] text-white",
  sent: "bg-[#059669] text-white",
  dead: "bg-[#E60000] text-white",
};

const CHANNEL_ICON = { email: Mail, push: Smartphone, inapp: Inbox, sms: MessageSquare };

const Kpi = ({ label, value, tone = "#0A0A0A", icon: Icon, testid }) => (
  <div className="p-4 bg-white border border-[#DEE2E6]" data-testid={testid}>
    <div className="flex items-center justify-between">
      <div className="text-[10px] uppercase tracking-[0.2em] text-[#868E96] font-bold">{label}</div>
      {Icon && <Icon className="h-4 w-4" style={{ color: tone }} />}
    </div>
    <div className="font-display font-black text-3xl mt-1" style={{ color: tone }}>{value ?? "—"}</div>
  </div>
);

const AlertSettingsCard = ({ onChanged }) => {
  const [cfg, setCfg] = useState(null);
  const [form, setForm] = useState({ enabled: true, threshold: 5, cooldown_hours: 12 });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/notifications/alert-settings");
      setCfg(data);
      setForm({ enabled: data.enabled, threshold: data.threshold, cooldown_hours: data.cooldown_hours });
    } catch (e) { /* silencieux */ }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/admin/notifications/alert-settings", form);
      toast.success("Seuil d'alerte enregistré");
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const runCheck = async (force) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/notifications/alert-check?force=${force ? "true" : "false"}`);
      if (data.alert) toast.success(`Alerte envoyée à ${data.notified_admins} admin(s) — ${data.dead} email(s) abandonné(s)`);
      else toast.message(`Aucune alerte (${data.dead} abandon(s) / seuil ${data.threshold}${data.skipped ? ` · ${data.skipped}` : ""})`);
      await load();
      onChanged && onChanged();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="p-5 bg-white border border-[#DEE2E6]" data-testid="mq-alert-card">
      <div className="font-display font-bold text-lg mb-2 flex items-center gap-2">
        <AlertTriangle className="h-5 w-5 text-[#F59E0B]" />Alerte « file bloquée »
      </div>
      <div className="text-[11px] text-[#495057] mb-3">
        Si trop d'emails épuisent leurs 3 tentatives sur 24 h, PAG vous prévient automatiquement
        par <b>email + notification interne + push</b> (le push passe même quand l'email est en panne).
        Contrôle toutes les heures.
      </div>

      <div className="grid sm:grid-cols-4 gap-3 items-end">
        <button
          type="button"
          onClick={() => setForm({ ...form, enabled: !form.enabled })}
          data-testid="mq-alert-toggle"
          className={`text-[11px] uppercase tracking-wider font-bold px-3 py-2 border ${
            form.enabled ? "bg-[#059669] text-white border-[#059669]" : "bg-white text-[#868E96] border-[#DEE2E6]"
          }`}
        >
          {form.enabled ? "Alerte activée" : "Alerte désactivée"}
        </button>
        <div>
          <Label className="text-[10px] uppercase tracking-wider">Seuil (échecs / 24 h)</Label>
          <Input type="number" min="1" value={form.threshold} data-testid="mq-alert-threshold"
                 onChange={(e) => setForm({ ...form, threshold: Number(e.target.value) || 1 })} />
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-wider">Anti-spam alerte (heures)</Label>
          <Input type="number" min="1" value={form.cooldown_hours} data-testid="mq-alert-cooldown"
                 onChange={(e) => setForm({ ...form, cooldown_hours: Number(e.target.value) || 1 })} />
        </div>
        <div className="flex gap-2">
          <Button onClick={save} disabled={busy} className="bg-[#002FA7] text-white" data-testid="mq-alert-save">Enregistrer</Button>
          <Button variant="outline" onClick={() => runCheck(true)} disabled={busy} data-testid="mq-alert-test">Tester</Button>
        </div>
      </div>

      <div className="mt-3 flex flex-wrap gap-4 text-[11px] text-[#495057]" data-testid="mq-alert-stats">
        <span>Abandons sur 24 h : <b className={cfg?.dead ? "text-[#E60000]" : "text-[#059669]"}>{cfg?.dead ?? "—"}</b></span>
        <span>Dernière alerte : <b>{cfg?.last_alert_at ? cfg.last_alert_at.slice(0, 16).replace("T", " ") : "jamais"}</b></span>
        <span>Historique : <b>{(cfg?.history || []).length}</b> alerte(s)</span>
      </div>
    </div>
  );
};

function AdminDigestCard() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data: d } = await api.get("/admin/notifications/digest");
      setData(d);
    } catch (e) { /* silencieux */ }
  };
  useEffect(() => { load(); }, []);

  const run = async () => {
    setBusy(true);
    try {
      const { data: r } = await api.post("/admin/notifications/digest/run?force=true");
      toast.success(`${r.digests_sent} digest(s) mis en file — ${r.items_sent} notification(s) regroupée(s)`);
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (!data) return null;
  return (
    <div className="p-4 border border-[#DEE2E6] bg-white flex flex-wrap items-center gap-4" data-testid="mq-digest-card">
      <Layers className="h-5 w-5 text-[#002FA7]" />
      <div className="flex-1 min-w-[240px]">
        <div className="font-display font-bold text-sm">Digest quotidien</div>
        <div className="text-[11px] text-[#495057]">
          {data.enabled_companies.length} entreprise(s) l'ont activé · <b>{data.pending}</b> notification(s) en attente
          de regroupement · job <code>daily_digest</code> chaque heure à H+05.
        </div>
        {data.enabled_companies.length > 0 && (
          <div className="mt-1 flex flex-wrap gap-1" data-testid="mq-digest-companies">
            {data.enabled_companies.map((c) => (
              <span key={c.entreprise_id} className="text-[10px] font-mono border border-[#DEE2E6] px-1.5 py-0.5">
                {c.entreprise?.code || c.entreprise_id?.slice(0, 8)} · {String(c.digest?.hour).padStart(2, "0")}h UTC
              </span>
            ))}
          </div>
        )}
      </div>
      <Button onClick={run} disabled={busy || !data.pending} variant="outline" data-testid="mq-digest-run">
        <Play className="h-4 w-4 mr-1" />{busy ? "Envoi…" : "Envoyer maintenant"}
      </Button>
    </div>
  );
}

export default function AdminNotifications() {
  const [stats, setStats] = useState(null);
  const [events, setEvents] = useState([]);
  const [items, setItems] = useState([]);
  const [journal, setJournal] = useState([]);
  const [status, setStatus] = useState("all");
  const [eventKey, setEventKey] = useState("all");
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [loadingQueue, setLoadingQueue] = useState(false);
  const [testTo, setTestTo] = useState("");

  // Filtres courants + garde de séquence : seule la dernière réponse est appliquée
  const filtersRef = useRef({ status: "all", eventKey: "all", q: "" });
  const seqRef = useRef(0);
  useEffect(() => { filtersRef.current = { status, eventKey, q }; }, [status, eventKey, q]);

  const loadQueue = async () => {
    const f = filtersRef.current;
    const seq = ++seqRef.current;
    const params = new URLSearchParams({ status: f.status, event_key: f.eventKey, limit: "60" });
    if (f.q.trim()) params.set("q", f.q.trim());
    setLoadingQueue(true);
    try {
      const { data } = await api.get(`/admin/mail-queue?${params.toString()}`);
      if (seq !== seqRef.current) return; // réponse obsolète → ignorée
      setItems(data || []);
    } finally {
      if (seq === seqRef.current) setLoadingQueue(false);
    }
  };

  const loadAll = async () => {
    try {
      const [c, s, j] = await Promise.all([
        api.get("/admin/notifications/catalog"),
        api.get("/admin/mail-queue/stats"),
        api.get("/admin/notification-events?limit=25"),
      ]);
      setEvents(c.data.events || []);
      setStats(s.data);
      setJournal(j.data || []);
      await loadQueue();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, []);
  useEffect(() => {
    filtersRef.current = { status, eventKey, q };
    loadQueue().catch(() => {});
    /* eslint-disable-next-line */
  }, [status, eventKey]);

  const refreshLight = async () => {
    try {
      const [s, j] = await Promise.all([
        api.get("/admin/mail-queue/stats"),
        api.get("/admin/notification-events?limit=25"),
      ]);
      setStats(s.data);
      setJournal(j.data || []);
      await loadQueue();
    } catch (e) { toast.error("Rafraîchissement partiel — réessayez"); }
  };

  const processNow = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/mail-queue/process?batch=10");
      toast.success(`Lot traité — ${data.sent} envoyé(s), ${data.retry} à réessayer, ${data.dead} abandonné(s)`);
      await refreshLight();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const retryOne = async (id) => {
    try {
      await api.post(`/admin/mail-queue/${id}/retry`);
      toast.success("Email remis en file d'attente");
      await refreshLight();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const purge = async () => {
    if (!window.confirm("Purger les emails envoyés et abandonnés de plus de 30 jours ?")) return;
    try {
      const { data } = await api.delete("/admin/mail-queue/purge?older_than_days=30&status=sent,dead");
      toast.success(`${data.deleted} entrée(s) purgée(s)`);
      await refreshLight();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const sendTest = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/notifications/test", testTo ? { to: testTo } : {});
      toast.success(`Événement émis vers ${data.to} — ${data.queued} email(s) en file`);
      await refreshLight();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const byStatus = stats?.by_status || {};
  const grouped = useMemo(() => {
    const g = {};
    events.forEach((e) => { (g[e.category] = g[e.category] || []).push(e); });
    return g;
  }, [events]);

  return (
    <div className="p-6 lg:p-10 max-w-6xl mx-auto space-y-6" data-testid="admin-notifications">
      <PageHeader
        title="Notifications & Mail"
        subtitle="Bus d'événements, file d'attente email (3 tentatives) et journal de distribution. Templates statiques FR/EN — aucune traduction IA à l'exécution."
      />

      {/* Bandeau moteur */}
      <div className="p-4 border-2 border-[#0A0A0A] bg-[#0A0A0A] text-white flex flex-wrap items-center gap-4" data-testid="mq-engine">
        <BellRing className="h-6 w-6 text-[#FFD700]" />
        <div className="flex-1 min-w-[240px]">
          <div className="font-display font-bold" key={`worker-${stats ? String(!!stats.queue_enabled) : "loading"}`}>
            {!stats ? "Worker — chargement…" : stats.queue_enabled ? "Worker actif" : "Worker désactivé (envoi synchrone)"}
          </div>
          <div className="text-[11px] text-white/60">
            Cadence {stats?.poll_seconds ?? 20}s · lots de {stats?.batch_size ?? 10} · {stats?.max_attempts ?? 3} tentatives
            (immédiat → +5 min → +30 min → abandon)
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button onClick={processNow} disabled={busy} data-testid="mq-process-now" className="bg-[#FFD700] text-[#0A0A0A] hover:bg-[#e6c200]">
            <Play className="h-4 w-4 mr-1" />Traiter maintenant
          </Button>
          <Button onClick={refreshLight} variant="outline" data-testid="mq-refresh" className="border-white/30 bg-transparent text-white hover:bg-white hover:text-[#0A0A0A]">
            <RefreshCw className="h-4 w-4 mr-1" />Rafraîchir
          </Button>
          <Button onClick={purge} variant="outline" data-testid="mq-purge" className="border-white/30 bg-transparent text-white hover:bg-[#E60000] hover:border-[#E60000]">
            <Trash2 className="h-4 w-4 mr-1" />Purger
          </Button>
        </div>
      </div>

      {/* Digest quotidien (Phase 43) */}
      <AdminDigestCard />

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <Kpi label="En attente (file + retry)" value={(byStatus.queued || 0) + (byStatus.retry || 0)} tone="#002FA7" icon={Clock} testid="mq-kpi-pending" />
        <Kpi label="En cours" value={byStatus.sending || 0} tone="#7C3AED" icon={Activity} testid="mq-kpi-sending" />
        <Kpi label="Envoyés" value={byStatus.sent || 0} tone="#059669" icon={CheckCircle2} testid="mq-kpi-sent" />
        <Kpi label="Abandonnés" value={byStatus.dead || 0} tone="#E60000" icon={AlertTriangle} testid="mq-kpi-dead" />
        <Kpi label="Dernières 24h" value={stats?.last_24h || 0} tone="#0A0A0A" icon={Mail} testid="mq-kpi-24h" />
      </div>

      {/* Diagnostic livraison */}
      {(stats?.top_errors || []).length > 0 && (
        <div className="p-4 border-2 border-[#E60000] bg-[#FFF5F5]" data-testid="mq-diagnostic">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-[#E60000] mt-0.5" />
            <div className="flex-1">
              <div className="font-display font-bold text-[#E60000]">Emails bloqués par le fournisseur</div>
              <div className="text-[11px] text-[#495057] mt-0.5">
                La file fonctionne, mais le fournisseur refuse les envois. Causes les plus fréquentes : domaine
                d'expédition non vérifié chez Resend, ou clé sans droits.
              </div>
              <ul className="mt-2 space-y-1">
                {stats.top_errors.map((e, i) => (
                  <li key={i} className="text-[11px] font-mono text-[#0A0A0A] bg-white border border-[#FFD1D1] px-2 py-1" data-testid={`mq-top-error-${i}`}>
                    <b>{e.count}×</b> {e.error}
                  </li>
                ))}
              </ul>
              <div className="mt-2 flex flex-wrap gap-2">
                <a href="/admin/emails" className="text-[10px] uppercase tracking-wider font-bold bg-[#0A0A0A] text-white px-3 py-1.5" data-testid="mq-fix-resend">
                  Vérifier le domaine Resend
                </a>
                <a href="/admin/sendgrid" className="text-[10px] uppercase tracking-wider font-bold bg-[#002FA7] text-white px-3 py-1.5" data-testid="mq-fix-sendgrid">
                  Basculer sur SendGrid
                </a>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Alerte file bloquée (Phase 39) */}
      <AlertSettingsCard onChanged={refreshLight} />

      {/* Test */}
      <div className="p-5 bg-white border border-[#DEE2E6]" data-testid="mq-test-card">
        <div className="font-display font-bold text-lg mb-2 flex items-center gap-2">
          <Send className="h-5 w-5 text-[#059669]" />Émettre un événement de test
        </div>
        <div className="text-[11px] text-[#495057] mb-3">
          Passe par le bus complet : template statique → file d'attente → worker. Laissez vide pour utiliser votre propre email admin.
        </div>
        <div className="flex flex-col sm:flex-row gap-2">
          <Input value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder="destinataire@exemple.fr (facultatif)" data-testid="mq-test-to" />
          <Button onClick={sendTest} disabled={busy} className="bg-[#059669] text-white" data-testid="mq-send-test">
            <Send className="h-4 w-4 mr-1" />Émettre le test
          </Button>
        </div>
      </div>

      {/* File d'attente */}
      <div className="p-5 bg-white border border-[#DEE2E6]" data-testid="mq-queue-card">
        <div className="font-display font-bold text-lg mb-3 flex items-center gap-2">
          <Mail className="h-5 w-5 text-[#002FA7]" />File d'attente ({items.length})
          {loadingQueue && <span className="text-[10px] uppercase tracking-wider text-[#868E96]" data-testid="mq-loading">Chargement…</span>}
        </div>
        <div className="flex flex-wrap gap-1.5 mb-3">
          {STATUS_TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setStatus(t.key)}
              data-testid={`mq-tab-${t.key}`}
              className={`text-[11px] uppercase tracking-wider font-bold px-3 py-1.5 border transition ${
                status === t.key ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "bg-white border-[#DEE2E6] hover:border-[#0A0A0A]"
              }`}
            >
              {t.label}{t.key !== "all" ? ` · ${byStatus[t.key] || 0}` : ""}
            </button>
          ))}
        </div>
        <div className="flex flex-col sm:flex-row gap-2 mb-3">
          <select
            value={eventKey}
            onChange={(e) => setEventKey(e.target.value)}
            data-testid="mq-filter-event"
            className="h-10 border border-[#DEE2E6] px-3 text-sm bg-white"
          >
            <option value="all">Tous les événements</option>
            {events.map((e) => <option key={e.key} value={e.key}>{e.label}</option>)}
          </select>
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher destinataire ou sujet" data-testid="mq-search" />
          <Button variant="outline" onClick={() => loadQueue()} data-testid="mq-search-btn">Filtrer</Button>
        </div>

        {items.length === 0 ? (
          <div className="text-xs text-[#868E96]" data-testid="mq-empty">Aucun email dans cette vue.</div>
        ) : (
          <div className={`divide-y divide-[#DEE2E6] border border-[#DEE2E6] max-h-[520px] overflow-y-auto transition-opacity ${loadingQueue ? "opacity-40" : "opacity-100"}`}>
            {items.map((m) => (
              <div key={m.id} className="p-3 hover:bg-[#F8F9FA]" data-testid={`mq-row-${m.id}`}>
                <div className="flex flex-wrap items-center gap-2">
                  <span className={`text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 ${STATUS_STYLE[m.status] || "bg-[#868E96] text-white"}`}>
                    {m.status}
                  </span>
                  <span className="text-[10px] font-mono text-[#868E96]">{(m.created_at || "").slice(0, 16).replace("T", " ")}</span>
                  <span className="text-[10px] uppercase tracking-wider font-bold text-[#002FA7]">{m.event_key}</span>
                  <span className="font-mono text-xs">{m.to}</span>
                  <span className="text-[10px] text-[#868E96]">{m.attempts}/{m.max_attempts} tentative(s)</span>
                  {m.provider && <span className="text-[9px] uppercase tracking-wider bg-[#F1F3F5] px-1.5 py-0.5">{m.provider}</span>}
                  <span className="text-[9px] uppercase tracking-wider border border-[#DEE2E6] px-1.5 py-0.5">{m.lang}</span>
                  <div className="flex-1" />
                  {["retry", "dead", "sent"].includes(m.status) && (
                    <Button size="sm" variant="outline" onClick={() => retryOne(m.id)} data-testid={`mq-retry-${m.id}`}>
                      <RotateCcw className="h-3.5 w-3.5 mr-1" />Relancer
                    </Button>
                  )}
                </div>
                <div className="text-sm font-semibold mt-1 truncate">{m.subject}</div>
                {m.last_error && <div className="text-[11px] text-[#E60000] mt-0.5 truncate">{m.last_error}</div>}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Journal du bus */}
      <div className="p-5 bg-white border border-[#DEE2E6]" data-testid="mq-journal-card">
        <div className="font-display font-bold text-lg mb-3 flex items-center gap-2">
          <Activity className="h-5 w-5 text-[#7C3AED]" />Journal des événements ({journal.length})
        </div>
        {journal.length === 0 ? (
          <div className="text-xs text-[#868E96]">Aucun événement émis pour le moment.</div>
        ) : (
          <div className="divide-y divide-[#DEE2E6] border border-[#DEE2E6] max-h-80 overflow-y-auto">
            {journal.map((j) => (
              <div key={j.id} className="p-2 flex flex-wrap items-center gap-2 text-xs hover:bg-[#F8F9FA]" data-testid={`mq-journal-${j.id}`}>
                <span className="text-[10px] font-mono text-[#868E96]">{(j.created_at || "").slice(0, 16).replace("T", " ")}</span>
                <span className="font-bold">{j.label}</span>
                <span className="text-[9px] uppercase tracking-wider bg-[#F1F3F5] px-1.5 py-0.5">{j.category}</span>
                <div className="flex-1" />
                <span className="text-[10px] text-[#495057]">
                  {j.recipients} destinataire(s) · {j.queued} mail · {j.inapp} in-app · {j.push} push
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Catalogue */}
      <div className="p-5 bg-white border border-[#DEE2E6]" data-testid="mq-catalog-card">
        <div className="font-display font-bold text-lg mb-3">Catalogue des événements ({events.length})</div>
        <div className="grid md:grid-cols-2 gap-4">
          {Object.entries(grouped).map(([cat, list]) => (
            <div key={cat} className="border border-[#DEE2E6]" data-testid={`mq-cat-${cat}`}>
              <div className="px-3 py-2 bg-[#F8F9FA] text-[10px] uppercase tracking-[0.2em] font-bold border-b border-[#DEE2E6]">{cat}</div>
              <div className="divide-y divide-[#DEE2E6]">
                {list.map((e) => (
                  <div key={e.key} className="px-3 py-2 flex items-center gap-2">
                    <div className="flex-1">
                      <div className="text-sm font-semibold">{e.label}</div>
                      <div className="text-[10px] font-mono text-[#868E96]">{e.key}</div>
                    </div>
                    <div className="flex gap-1">
                      {e.channels.map((c) => {
                        const Icon = CHANNEL_ICON[c] || Mail;
                        return <Icon key={c} className="h-3.5 w-3.5 text-[#002FA7]" title={c} />;
                      })}
                    </div>
                    <span className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 ${e.scope === "platform" ? "bg-[#0A0A0A] text-white" : "bg-[#FFD700] text-[#0A0A0A]"}`}>
                      {e.scope === "platform" ? "PAG" : "Entreprise"}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
        <div className="mt-3 text-[11px] text-[#495057]">
          <b>PAG</b> = événement plateforme toujours actif · <b>Entreprise</b> = activable/désactivable par le chef
          depuis son profil entreprise.
        </div>
      </div>
    </div>
  );
}
